import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { Button } from "../../../components/Button";
import ConfirmModal from "../../../components/ConfirmModal";
import * as Fonts from "../../../components/Fonts";
import Input from "../../../components/Form/Input/input";
import { Select } from "../../../components/Form/Input/select";
import { TextArea } from "../../../components/Form/Input/textArea";
import Label from "../../../components/Form/Label";
import { Form, FormGroup } from "../../../components/Form/styles";
import { LoadingModal } from "../../../components/LoadingModal";
import { Crumb, ScreenHeader } from "../../../components/SubHeader";
import { User, api } from "../../../services/api";
import { isEmailValid } from "../../../utils/email";
import { Card, CardFooter, Container, Section } from "../styles";

const SUBJECT_OPTIONS = ["SELECT", "ABOUT_USE", "HELP_SUBSCRIPTION", "FEEDBACK", "BUG_REPORT", "OTHERS"];

/** Support by e-mail: the form sent to the Keepo team through the API. */
export const HelpEmailPage = () => {
  const { t } = useTranslation();

  const [isSubmitting, setSubmitting] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);

  const subjectOptions = useMemo(() => {
    return SUBJECT_OPTIONS.map(option => ({
      id: option,
      description: t(`pages.help.form.subjectOptions.${option}`),
    }));
  }, [t]);

  const [user, setUser] = useState<User>();
  // Screens that link straight here pass where to go back to; otherwise, Help
  const back = (useLocation().state as { back?: Crumb } | null)?.back;

  const [subject, setSubject] = useState<string>(subjectOptions[0].id);
  const [message, setMessage] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  const isSubmitDisabled = subject === "SELECT" || !message || !isEmailValid(email);

  const loadUser = useCallback(async () => {
    const user = await api.getUser();
    setUser(user);
    setEmail(user.email);
  }, []);

  // Thanks the user and stays on the screen with a clean form (the e-mail is kept)
  const closeSuccessModal = () => {
    setSubmitted(false);
    setSubject(subjectOptions[0].id);
    setMessage("");
  };

  const saveHandler = async () => {
    if (isSubmitDisabled) {
      return;
    }
    setSubmitting(true);
    try {
      await api.submitHelp({
        subject,
        message,
        email,
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return !user ? (
    <LoadingModal />
  ) : (
    <Container>
      <ScreenHeader
        title={t("pages.help.options.email.title")}
        back={back || { label: t("pages.help.header.title"), to: "/help" }}
      />
      <Section>
        <Fonts.Label>{t("pages.help.sections.contact")}</Fonts.Label>
        <Form onSubmit={e => e.preventDefault()}>
          <Card>
            <FormGroup>
              <Select
                id="subject"
                name="subject"
                value={subject}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSubject(e.target.value)}>
                {subjectOptions.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.description}
                  </option>
                ))}
              </Select>
              <Label htmlFor="subject" text={t("pages.help.form.subject")} />
            </FormGroup>
            <FormGroup>
              <TextArea
                id="message"
                name="message"
                autoComplete="off"
                rows={7}
                value={message}
                //@ts-ignore
                onChange={e => setMessage(e.target.value)}
                maxLength={1000}
              />
              <Label
                maxLength={1000}
                usedLength={message?.length || 0}
                htmlFor="message"
                text={t("pages.help.form.message")}
              />
            </FormGroup>
            <FormGroup>
              <Input
                type="email"
                id="email"
                maxLength={100}
                ignoreLengthInfo
                value={email}
                onChange={e => setEmail(e.target.value)}
                label={t("pages.help.form.email")}
              />
            </FormGroup>
            <CardFooter>
              <Button type="INFO" size="MD" disabled={isSubmitDisabled || isSubmitting} onClick={saveHandler}>
                {t("pages.help.form.submitButton")}
              </Button>
            </CardFooter>
          </Card>
        </Form>
      </Section>
      {isSubmitted && (
        <ConfirmModal
          title={t("pages.help.successModal.message")}
          type="INFO"
          confirmButtonTitle={t("pages.help.successModal.buttonTitle")}
          onConfirm={closeSuccessModal}
          onExit={closeSuccessModal}
        />
      )}
    </Container>
  );
};
